import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Search, Shield, AlertTriangle, CheckCircle2, Clock, MapPin, Wrench,
  Droplets, Battery, Radio, UtensilsCrossed, HeartPulse, FileLock2, PawPrint, Car,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import ApexLoader from "@/components/ApexLoader";

const SUPPLY_CATEGORIES = [
  {
    key: "windows",
    label: "Windows & Doors",
    Icon: Shield,
    accent: "#dc2626",
    items: [
      { name: "5/8\" plywood sheets (one per window)", priority: "critical", note: "Pre-cut to size if possible. Tested to withstand wind-borne debris up to ~135 mph." },
      { name: "Hurricane / impact-resistant film", priority: "recommended", note: "Adhesive film bonded to inside of glass — slows shatter." },
      { name: "Wood screws / impact drill", priority: "critical", note: "3-inch wood screws, 12-inch spacing for plywood install." },
      { name: "Garage door bracing kit", priority: "recommended", note: "Garage door is the weakest point on most homes." },
    ],
  },
  {
    key: "water",
    label: "Water Defense",
    Icon: Droplets,
    accent: "#3b82f6",
    items: [
      { name: "Sandbags (10–20 per door / low entry)", priority: "critical", note: "Free at most municipalities during a watch." },
      { name: "Sump pump · battery backup", priority: "critical", note: "Power loss is when most basements flood." },
      { name: "Water-activated flood barriers (e.g. HydraBarrier, Quick Dam)", priority: "recommended", note: "Reusable alternative to sandbags." },
      { name: "Wet/dry vac · 12+ gallon", priority: "recommended", note: "First 48 hours of cleanup post-flood." },
    ],
  },
  {
    key: "power",
    label: "Power & Light",
    Icon: Battery,
    accent: "#f59e0b",
    items: [
      { name: "Generator (gas / propane / dual-fuel) · 5kW+", priority: "critical", note: "NEVER run indoors or in attached garage." },
      { name: "Extra fuel · 10+ gallons (rotated, stabilized)", priority: "critical", note: "Stations sell out 24h before landfall." },
      { name: "Power station / portable battery (1500Wh+)", priority: "recommended", note: "Quiet, indoor-safe alternative for fridge + comms." },
      { name: "Flashlights · LED · 1 per person + headlamps", priority: "critical", note: "Plus spare batteries (AA/AAA/lithium)." },
      { name: "Battery-powered or crank lanterns", priority: "recommended" },
    ],
  },
  {
    key: "comms",
    label: "Communication",
    Icon: Radio,
    accent: "#a855f7",
    items: [
      { name: "NOAA weather radio · battery / hand-crank", priority: "critical", note: "Cell towers and internet often go down." },
      { name: "Backup phone battery banks (20,000mAh+)", priority: "critical" },
      { name: "Two-way radios for the household", priority: "recommended", note: "FRS / GMRS works if cell is down." },
      { name: "Printed contact list · paper", priority: "recommended", note: "Family, doctors, insurance, restoration co." },
    ],
  },
  {
    key: "food",
    label: "Food & Water",
    Icon: UtensilsCrossed,
    accent: "#10b981",
    items: [
      { name: "1 gallon of water per person per day · 7 days minimum", priority: "critical" },
      { name: "Non-perishable food · 7 days minimum", priority: "critical", note: "Canned goods, protein bars, peanut butter, MREs." },
      { name: "Manual can opener", priority: "critical" },
      { name: "Camp stove + fuel canisters", priority: "recommended" },
      { name: "Cooler + ice (or block ice for 4+ days)", priority: "recommended" },
    ],
  },
  {
    key: "medical",
    label: "Medical & First Aid",
    Icon: HeartPulse,
    accent: "#ec4899",
    items: [
      { name: "First aid kit · stocked", priority: "critical" },
      { name: "Prescription meds · 14-day supply", priority: "critical", note: "Refill BEFORE the storm window. Pharmacies close." },
      { name: "Hand sanitizer, masks, gloves", priority: "recommended" },
      { name: "Insect repellent + sunscreen", priority: "recommended", note: "Post-storm cleanup means standing water + bugs." },
    ],
  },
  {
    key: "documents",
    label: "Documents & Cash",
    Icon: FileLock2,
    accent: "#e8e8e8",
    items: [
      { name: "Insurance policy · digital + waterproof printed copy", priority: "critical", note: "Photograph every room before storm hits." },
      { name: "Driver's license, passport, SS card · waterproof bag", priority: "critical" },
      { name: "Cash · small bills · $200+", priority: "critical", note: "ATMs and card readers go down with power." },
      { name: "Pre-storm photos / video of every room", priority: "critical", note: "Time-stamped. Saves you in claims later." },
    ],
  },
  {
    key: "pets",
    label: "Pets",
    Icon: PawPrint,
    accent: "#f97316",
    items: [
      { name: "7-day food + water supply per pet", priority: "critical" },
      { name: "Carrier / crate per pet", priority: "critical" },
      { name: "Vaccination + medication records", priority: "recommended" },
      { name: "Up-to-date ID tag + microchip info", priority: "critical" },
    ],
  },
  {
    key: "vehicle",
    label: "Vehicle",
    Icon: Car,
    accent: "#64748b",
    items: [
      { name: "Full tank of gas · 48h before landfall", priority: "critical" },
      { name: "Park on highest ground available · away from trees", priority: "critical" },
      { name: "Tire jack, spare, jumper cables", priority: "recommended" },
      { name: "Paper map of region", priority: "recommended" },
    ],
  },
];

const TOTAL_ITEMS = SUPPLY_CATEGORIES.reduce((s, c) => s + c.items.length, 0);

const PATH_BG = {
  SEVERE: "rgba(220,38,38,0.12)",
  ELEVATED: "rgba(245,158,11,0.12)",
  MODERATE: "rgba(251,146,60,0.10)",
  CLEAR: "rgba(16,185,129,0.10)",
};
const PATH_FG = {
  SEVERE: "#fca5a5",
  ELEVATED: "#fcd34d",
  MODERATE: "#fdba74",
  CLEAR: "#6ee7b7",
};

export default function StormReadinessHub() {
  const [zip, setZip] = useState("");
  const [pathResult, setPathResult] = useState(null);
  const [pathLoading, setPathLoading] = useState(false);
  const [pathError, setPathError] = useState("");
  const [activeCat, setActiveCat] = useState(SUPPLY_CATEGORIES[0].key);
  const [checked, setChecked] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      const saved = window.localStorage.getItem("apex_readiness_checklist");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem("apex_readiness_checklist", JSON.stringify(checked));
    } catch (err) {
      console.warn("Could not persist readiness checklist to localStorage", err);
    }
  }, [checked]);

  const checkPath = async (e) => {
    e.preventDefault();
    setPathLoading(true);
    setPathError("");
    setPathResult(null);
    try {
      const { data } = await api.post("/storm-readiness/path-check", { zip });
      setPathResult(data);
    } catch (err) {
      setPathError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setPathLoading(false);
    }
  };

  const toggle = (id) => setChecked((c) => ({ ...c, [id]: !c[id] }));
  const completeCount = Object.values(checked).filter(Boolean).length;
  const pct = Math.round((completeCount / TOTAL_ITEMS) * 100);

  const activeCategory = SUPPLY_CATEGORIES.find((c) => c.key === activeCat);

  return (
    <section
      id="readiness"
      className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden"
      data-testid="readiness-section"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          background:
            "radial-gradient(700px 350px at 15% 30%, rgba(220,38,38,0.10), transparent 65%), radial-gradient(700px 350px at 85% 70%, rgba(255,255,255,0.04), transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="block w-8 h-px bg-[#dc2626]" />
          <span className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-400">
            shelter-in-place · readiness hub
          </span>
        </div>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
          <h2 className="font-display text-5xl md:text-6xl text-white tracking-tight max-w-3xl leading-[0.95]">
            Staying put? <span className="silver-text italic">We've got the playbook.</span>
          </h2>
          <p className="font-body text-base text-zinc-400 max-w-md">
            Check your zip against active storm paths, work the supply checklist, and call a pro
            if you need help boarding up before landfall.
          </p>
        </div>

        {/* Path checker + progress card */}
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-4 mb-8">
          {/* Hurricane path checker */}
          <div className="bg-[#121214] border border-white/10 p-7" data-testid="path-checker-card">
            <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4 flex items-center gap-2">
              <AlertTriangle size={12} className="text-[#dc2626]" /> hurricane / storm path checker
            </div>
            <form onSubmit={checkPath} className="flex flex-col sm:flex-row gap-3 mb-5">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  required
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder="Your home's ZIP"
                  inputMode="numeric"
                  className="input-apex w-full pl-10 pr-4 py-3 font-mono-apex text-sm tracking-[0.15em]"
                  data-testid="path-zip-input"
                />
              </div>
              <button
                type="submit"
                disabled={pathLoading || !zip.trim()}
                className="btn-primary-apex px-6 py-3 font-mono-apex text-[10px] uppercase tracking-[0.25em] flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="path-check-btn"
              >
                {pathLoading ? <ApexLoader inline size={14} /> : <Shield size={14} />}
                Check my zone
              </button>
            </form>

            {pathError && <div className="font-mono-apex text-xs text-[#dc2626]">{pathError}</div>}

            {pathResult && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="border-l-2 p-5 mt-2"
                style={{
                  background: PATH_BG[pathResult.severity] || PATH_BG.CLEAR,
                  borderLeftColor: pathResult.in_path ? "#dc2626" : "#10b981",
                }}
                data-testid="path-result"
              >
                {pathResult.in_path ? (
                  <>
                    <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] mb-2" style={{ color: PATH_FG[pathResult.severity] }}>
                      ⚠ in path · severity {pathResult.severity}
                    </div>
                    <div className="font-display text-2xl text-white mb-2">{pathResult.storm_name}</div>
                    <div className="flex flex-wrap gap-4 font-mono-apex text-[11px] text-zinc-300 mb-3">
                      <span className="flex items-center gap-1.5"><MapPin size={11} /> {pathResult.zip}</span>
                      <span className="flex items-center gap-1.5"><Clock size={11} /> ETA ~{pathResult.eta_hours}h</span>
                    </div>
                    <a
                      href="#find-a-pro"
                      className="inline-flex items-center gap-2 btn-primary-apex px-5 py-2.5 font-mono-apex text-[10px] uppercase tracking-[0.25em] mt-2"
                      data-testid="secure-home-cta"
                    >
                      <Wrench size={12} /> Need help securing my home
                    </a>
                  </>
                ) : (
                  <>
                    <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] mb-2 text-emerald-300">
                      ✓ no active threat · {pathResult.zip}
                    </div>
                    <div className="font-display text-xl text-white mb-2">You're clear for now.</div>
                    <p className="font-body text-sm text-zinc-300 leading-relaxed">
                      No active hurricane, severe storm, or tornado warning in your zone. Use this
                      window to work the readiness checklist below — preparation is cheaper than
                      cleanup.
                    </p>
                  </>
                )}
              </motion.div>
            )}
          </div>

          {/* Progress card */}
          <div className="bg-[#121214] border border-white/10 p-7 flex flex-col justify-between">
            <div>
              <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">
                your readiness checklist
              </div>
              <div className="flex items-baseline gap-3 mb-4">
                <span className="font-mono-apex text-5xl silver-text">{pct}%</span>
                <span className="font-mono-apex text-xs text-zinc-500">
                  {completeCount} / {TOTAL_ITEMS} items
                </span>
              </div>
              <div className="h-1.5 bg-white/5 mb-4 relative overflow-hidden">
                <motion.div
                  initial={false}
                  animate={{ width: `${pct}%` }}
                  className="h-full bg-[#dc2626]"
                />
              </div>
              <p className="font-body text-sm text-zinc-400 leading-relaxed">
                Progress saved automatically — pick up where you left off, on any device. Aim for 100%
                ahead of any active watch in your area.
              </p>
            </div>
            {completeCount > 0 && (
              <button
                onClick={() => setChecked({})}
                className="font-mono-apex text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white mt-4 self-start"
                data-testid="reset-checklist-btn"
              >
                ↺ reset checklist
              </button>
            )}
          </div>
        </div>

        {/* Category tabs + supply list */}
        <div className="bg-[#121214] border border-white/10" data-testid="supplies-card">
          <div className="flex overflow-x-auto scroll-thin border-b border-white/5">
            {SUPPLY_CATEGORIES.map((c) => {
              const Icon = c.Icon;
              const catItems = c.items.length;
              const catChecked = c.items.filter((it) => checked[`${c.key}::${it.name}`]).length;
              const isActive = activeCat === c.key;
              return (
                <button
                  key={c.key}
                  onClick={() => setActiveCat(c.key)}
                  className={`flex items-center gap-2 px-5 py-4 border-b-2 whitespace-nowrap transition-colors ${
                    isActive
                      ? "border-[#dc2626] text-white bg-white/[0.02]"
                      : "border-transparent text-zinc-400 hover:text-white"
                  }`}
                  data-testid={`cat-tab-${c.key}`}
                >
                  <Icon size={14} strokeWidth={1.5} style={{ color: isActive ? c.accent : undefined }} />
                  <span className="font-mono-apex text-[10px] uppercase tracking-[0.2em]">{c.label}</span>
                  <span className="font-mono-apex text-[9px] text-zinc-600 ml-1">
                    {catChecked}/{catItems}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="p-7">
            <div className="flex items-center gap-3 mb-5">
              <activeCategory.Icon size={20} strokeWidth={1.5} style={{ color: activeCategory.accent }} />
              <h3 className="font-display text-2xl text-white">{activeCategory.label}</h3>
            </div>
            <div className="space-y-2">
              {activeCategory.items.map((it) => {
                const id = `${activeCategory.key}::${it.name}`;
                const isChecked = !!checked[id];
                const critical = it.priority === "critical";
                return (
                  <label
                    key={it.name}
                    className={`flex items-start gap-4 p-4 border cursor-pointer transition-all ${
                      isChecked
                        ? "border-emerald-500/30 bg-emerald-500/[0.04]"
                        : "border-white/5 bg-[#0a0a0b] hover:border-white/15"
                    }`}
                    data-testid={`supply-item-${activeCategory.key}-${it.name.slice(0, 20).replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggle(id)}
                      className="sr-only"
                    />
                    <span
                      className={`mt-0.5 w-5 h-5 border flex items-center justify-center shrink-0 transition-colors ${
                        isChecked
                          ? "border-emerald-500 bg-emerald-500/20"
                          : "border-white/20 bg-transparent"
                      }`}
                    >
                      {isChecked && <CheckCircle2 size={14} className="text-emerald-400" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`font-body text-sm ${isChecked ? "text-zinc-400 line-through" : "text-white"}`}>
                          {it.name}
                        </span>
                        {critical && (
                          <span className="font-mono-apex text-[8px] uppercase tracking-[0.2em] text-[#dc2626] border border-[#dc2626]/40 px-1.5 py-0.5">
                            critical
                          </span>
                        )}
                      </div>
                      {it.note && (
                        <div className="font-mono-apex text-[10px] text-zinc-500 leading-relaxed">{it.note}</div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {/* Help CTA */}
        <div className="mt-6 p-7 md:p-9 border border-white/10 bg-[#121214] flex flex-col md:flex-row gap-6 items-start md:items-center">
          <div className="w-12 h-12 border border-[#dc2626]/40 bg-[#dc2626]/10 flex items-center justify-center shrink-0">
            <Wrench size={20} className="text-[#dc2626]" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">help on the ground</div>
            <div className="font-display text-2xl text-white mb-1">Can't board up alone?</div>
            <p className="font-body text-sm text-zinc-400 leading-relaxed">
              We can match you with a local pro who'll come secure your home — board windows, lay
              sandbags, brace garage doors — before the storm hits. No cost to find. No obligation.
            </p>
          </div>
          <a
            href="#find-a-pro"
            className="px-6 py-3 btn-primary-apex font-mono-apex text-[10px] uppercase tracking-[0.25em] whitespace-nowrap"
            data-testid="readiness-help-cta"
          >
            Find help now
          </a>
        </div>
      </div>
    </section>
  );
}
